/**
 * formStyles.ts - Unified form styles (single source of truth)
 *
 * All form components should use these styles to ensure consistency
 * across the entire application.
 */

import type { CSSProperties } from 'react';

export const formStyles = {
  // Input base
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  } as CSSProperties,

  inputFocus: {
    borderColor: 'var(--primary, #3b82f6)',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  } as CSSProperties,

  inputError: {
    borderColor: '#ef4444',
  } as CSSProperties,

  inputDisabled: {
    background: 'var(--bg-muted, #f3f4f6)',
    cursor: 'not-allowed',
    opacity: 0.7,
  } as CSSProperties,

  // Input with icon
  inputWrapper: {
    position: 'relative',
  } as CSSProperties,

  inputWithIcon: {
    paddingLeft: '36px',
  } as CSSProperties,

  inputIcon: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--text-muted, #9ca3af)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as CSSProperties,

  // Textarea
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    outline: 'none',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  } as CSSProperties,

  // Select
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'white',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  } as CSSProperties,

  // Layout
  row: {
    display: 'flex',
    gap: '12px',
  } as CSSProperties,

  column: {
    flex: 1,
  } as CSSProperties,

  // Field wrapper
  field: {
    marginBottom: '16px',
  } as CSSProperties,

  fieldLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text, #374151)',
    marginBottom: '6px',
  } as CSSProperties,

  fieldRequired: {
    color: '#ef4444',
    marginLeft: '2px',
  } as CSSProperties,

  fieldError: {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px',
  } as CSSProperties,

  fieldHint: {
    fontSize: '12px',
    color: 'var(--text-muted, #6b7280)',
    marginTop: '4px',
  } as CSSProperties,

  // Tags
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '6px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '6px',
    minHeight: '36px',
    background: 'white',
  } as CSSProperties,

  tag: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: 'var(--bg, #e8e8e8)',
    borderRadius: '9999px',
    fontSize: '12px',
  } as CSSProperties,

  tagRemove: {
    background: 'none',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    color: '#999',
    display: 'flex',
    alignItems: 'center',
  } as CSSProperties,

  tagInput: {
    border: 'none',
    outline: 'none',
    flex: 1,
    minWidth: '80px',
    fontSize: '13px',
    background: 'transparent',
  } as CSSProperties,

  // Buttons base
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    outline: 'none',
  } as CSSProperties,

  buttonPrimary: {
    background: 'var(--brand, #8b5cf6)',
    color: 'white',
  } as CSSProperties,

  buttonSecondary: {
    background: 'white',
    color: 'var(--text, #374151)',
    border: '1px solid var(--line, #e5e7eb)',
  } as CSSProperties,

  buttonDanger: {
    background: '#fee2e2',
    color: '#dc2626',
  } as CSSProperties,

  buttonSuccess: {
    background: '#dcfce7',
    color: '#16a34a',
  } as CSSProperties,

  buttonGhost: {
    background: 'transparent',
    color: 'var(--text, #374151)',
  } as CSSProperties,

  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  } as CSSProperties,

  buttonSmall: {
    padding: '6px 12px',
    fontSize: '13px',
  } as CSSProperties,

  buttonLarge: {
    padding: '10px 20px',
    fontSize: '15px',
  } as CSSProperties,

  buttonIcon: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: 'var(--text-muted, #6b7280)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.15s, background 0.15s',
  } as CSSProperties,

  // Button group / actions
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as CSSProperties,

  actionsSpaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as CSSProperties,
} as const;

// Helper to merge styles
export function mergeStyles(...styles: (CSSProperties | undefined | false)[]): CSSProperties {
  return styles.reduce<CSSProperties>((acc, style) => {
    if (style) {
      return { ...acc, ...style };
    }
    return acc;
  }, {});
}
